import React, { useState, useMemo, useEffect, useCallback } from "react";
import { WifiIcon, CpuChipIcon, ArrowLeftIcon, ChevronRightIcon, ChevronLeftIcon, ExclamationCircleIcon, SignalIcon } from "@heroicons/react/24/outline";
import { useWebSocket } from "../hooks/useWebSocket";
import { AssetList } from "./AssetList";
import { SensorChart, FeatureChart, RiskChart } from "./SensorChart";
import { RiskGauge, FeaturePanel } from "./RiskGauge";
import { ControlPanel } from "./ControlPanel";
import { FleetDashboard } from "./FleetDashboard";
import { TemperatureMarginIndicator } from "./TemperatureMargin";
import { CyclesExplainer } from "./InfoTooltip";
import { USEFUL_SENSORS, SENSOR_INFO } from "../utils/constants";

// Number of top sensors to display in charts
const NUM_DISPLAY_SENSORS = 4;

/**
 * Calculate sensor importance score based on feature data.
 * Higher absolute slope and higher std indicate more interesting/concerning sensors.
 */
function calculateSensorImportance(sensorFeatures) {
  if (!sensorFeatures) return {};

  const scores = {};

  for (const [sensor, features] of Object.entries(sensorFeatures)) {
    // Skip if no features
    if (!features) continue;

    // Base importance from sensor type (critical sensors get bonus)
    const sensorInfo = SENSOR_INFO[sensor];
    const baseScore = sensorInfo?.importance === "critical" ? 100 :
                      sensorInfo?.importance === "high" ? 50 :
                      sensorInfo?.importance === "medium" ? 25 : 10;

    // Slope magnitude - higher absolute slope is more important (degradation signal)
    const slopeScore = Math.abs(features.slope || 0) * 10000; // Scale up small slopes

    // Std magnitude - higher variance can indicate instability
    const stdScore = (features.std || 0) * 10;

    // Combined score
    scores[sensor] = baseScore + slopeScore + stdScore;
  }

  return scores;
}

/**
 * Get top N sensors by importance score
 */
function getTopSensors(sensorFeatures, n = NUM_DISPLAY_SENSORS) {
  const scores = calculateSensorImportance(sensorFeatures);

  // Sort sensors by score descending
  const sorted = Object.entries(scores)
    .sort(([, a], [, b]) => b - a)
    .map(([sensor]) => sensor);

  return sorted.slice(0, n);
}

/**
 * Main Dashboard component orchestrating all visualizations.
 */
export function Dashboard() {
  const {
    isConnected,
    serverState,
    assets,
    sensorHistory,
    featureHistory,
    riskHistory,
    recentEvents,
    stats,
    sendCommand,
  } = useWebSocket();

  const [selectedAsset, setSelectedAsset] = useState(null);
  const [isStarting, setIsStarting] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  // Clear loading state when streaming actually starts
  useEffect(() => {
    if (serverState?.streaming) {
      setIsStarting(false);
    }
  }, [serverState?.streaming]);

  // Wrap sendCommand to track loading state
  const handleSendCommand = useCallback((command) => {
    if (command.action === "start") {
      setIsStarting(true);
    }
    sendCommand(command);
  }, [sendCommand]);

  // Get data for selected asset
  const selectedSensorData = selectedAsset
    ? sensorHistory[selectedAsset] || []
    : [];
  const selectedFeatureData = selectedAsset
    ? featureHistory[selectedAsset] || []
    : [];
  const selectedRiskData = selectedAsset
    ? riskHistory[selectedAsset] || []
    : [];
  const selectedAssetInfo = selectedAsset ? assets[selectedAsset] : null;

  // Get latest risk for selected asset
  // Merge riskHistory data with asset risk to include AI explanation and ML predictions
  const latestRisk = (() => {
    const historyRisk = selectedRiskData.length > 0 ? selectedRiskData[selectedRiskData.length - 1] : null;
    const assetRisk = selectedAssetInfo?.risk;

    if (historyRisk && assetRisk) {
      // Merge: use history data but include explanation and ML fields from asset
      return {
        ...historyRisk,
        explanation: assetRisk.explanation,
        // Ensure ML fields are included (from either source)
        ml_risk_score: historyRisk.ml_risk_score ?? assetRisk.ml_risk_score,
        ml_predicted_rul: historyRisk.ml_predicted_rul ?? assetRisk.ml_predicted_rul,
        used_ml_model: historyRisk.used_ml_model ?? assetRisk.used_ml_model,
      };
    }
    return historyRisk || assetRisk;
  })();

  // Dynamically select top sensors based on current importance
  const topSensors = useMemo(() => {
    const sensorFeatures = selectedAssetInfo?.features?.sensor_features;
    if (!sensorFeatures || Object.keys(sensorFeatures).length === 0) {
      // Fallback to critical sensors when no data
      return ["sensor_7", "sensor_11", "sensor_12", "sensor_2"];
    }
    return getTopSensors(sensorFeatures, NUM_DISPLAY_SENSORS);
  }, [selectedAssetInfo?.features?.sensor_features]);

  return (
    <div className="dashboard">
      {/* Header */}
      <header className="dashboard-header">
        <div className="header-left">
          <CpuChipIcon className="h-8 w-8 logo-icon" />
          <div className="header-title">
            <h1>Catalyst Engine Monitor</h1>
            <span className="subtitle">Real-Time Failure Prediction System</span>
          </div>
        </div>

        <div className="header-right">
          <div className="connection-status">
            {isConnected ? (
              <>
                <SignalIcon className="h-5 w-5 status-icon connected" />
                <span>Connected</span>
              </>
            ) : (
              <>
                <ExclamationCircleIcon className="h-5 w-5 status-icon disconnected" />
                <span>Disconnected</span>
              </>
            )}
          </div>
          <div className="stats">
            <SignalIcon className="h-4 w-4" />
            <span>{stats.eventsReceived.toLocaleString()} events</span>
          </div>
        </div>
      </header>

      {/* Cycles Explainer - shows once when streaming starts */}
      {serverState?.streaming && (
        <CyclesExplainer
          currentCycle={serverState?.current_cycle || selectedAssetInfo?.features?.window_end}
        />
      )}

      {/* Main Content */}
      <div className="dashboard-content">
        {/* Left Sidebar - Control Panel + Asset List */}
        <aside className={`sidebar ${sidebarOpen ? 'open' : 'collapsed'}`}>
          <ControlPanel
            serverState={serverState}
            onSendCommand={handleSendCommand}
          />
          <AssetList
            assets={assets}
            selectedAsset={selectedAsset}
            onSelectAsset={setSelectedAsset}
          />
          {/* Close button inside sidebar */}
          {sidebarOpen && (
            <button
              className="sidebar-edge-toggle inside"
              onClick={() => setSidebarOpen(false)}
              title="Close panel"
            >
              <ChevronLeftIcon className="h-3.5 w-3.5" />
            </button>
          )}
        </aside>

        {/* Edge toggle when sidebar is collapsed */}
        {!sidebarOpen && (
          <button
            className="sidebar-edge-toggle outside"
            onClick={() => setSidebarOpen(true)}
            title="Open panel"
          >
            <ChevronRightIcon className="h-3.5 w-3.5" />
          </button>
        )}

        {/* Main Panel */}
        <main className="main-panel">
          {!selectedAsset ? (
            <FleetDashboard
              assets={assets}
              serverState={serverState}
              recentEvents={recentEvents}
              riskHistory={riskHistory}
              onSelectAsset={setSelectedAsset}
              isStarting={isStarting}
            />
          ) : (
            <>
              {/* Selected Asset Header */}
              <div className="asset-header">
                <button
                  className="back-btn"
                  onClick={() => setSelectedAsset(null)}
                  title="Back to Fleet Dashboard"
                >
                  <ArrowLeftIcon className="h-4 w-4" />
                </button>
                <h2>Engine {selectedAsset}</h2>
                {selectedAssetInfo && (
                  <span
                    className={`status-badge large ${selectedAssetInfo.status.toLowerCase()}`}
                  >
                    {selectedAssetInfo.status}
                  </span>
                )}
              </div>

              {/* Top Row - Risk and Features */}
              <div className="metrics-row">
                <RiskGauge
                  riskData={latestRisk}
                  assetId={selectedAsset}
                  aiEnabled={serverState?.ai_enabled !== false}
                  groundTruthRul={selectedAssetInfo?.ground_truth_rul}
                />
                <FeaturePanel
                  features={selectedAssetInfo?.features}
                  assetId={selectedAsset}
                />
              </div>

              {/* Temperature Margin Indicator - Critical for turbine monitoring */}
              {selectedAssetInfo?.features?.sensor_features && (
                <TemperatureMarginIndicator
                  sensorFeatures={selectedAssetInfo.features.sensor_features}
                />
              )}

              {/* Charts Row */}
              <div className="charts-row">
                {/* Sensor Values Chart - shows top 4 most important sensors dynamically */}
                <SensorChart
                  title="Sensor Readings"
                  data={selectedSensorData}
                  dataKeys={topSensors}
                  height={200}
                />

                {/* Risk Score Over Time */}
                <RiskChart data={selectedRiskData} />
              </div>

              {/* Feature Charts Row */}
              <div className="charts-row">
                <FeatureChart
                  title="Trend (Slope)"
                  data={selectedFeatureData}
                  sensors={topSensors}
                  featureType="slope"
                />
                <FeatureChart
                  title="Variance (Std Dev)"
                  data={selectedFeatureData}
                  sensors={topSensors}
                  featureType="std"
                />
              </div>
            </>
          )}
        </main>
      </div>
    </div>
  );
}
